import { useAIChatbotAccess } from "@faire/help-center-self-serve/hooks/useAIChatbotAccess";
import { useBrandMilestone } from "@faire/help-center-utils/hooks/useBrandMilestones";
import { useRetailerMilestone } from "@faire/help-center-utils/hooks/useRetailerMilestones";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getIsUserImpersonating } from "@faire/help-center-utils/serialized-data/getIsUserImpersonating";
import {
  getRetailerToken,
  isRetailer,
} from "@faire/help-center-utils/serialized-data/getRetailer";
import { getUserToken } from "@faire/help-center-utils/serialized-data/getUser";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logError } from "@faire/web--source/common/logging";
import { orderNumber } from "@faire/web--source/common/tokenUtil";
import { isProduction } from "@faire/web--source/common/user-agent/isProduction";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useQuery as useArticleLocationTagsQuery } from "@faire/web-api--source/endpoints/www/api/help-center/chat-bot/article-location-tags/get-hooks";
import { useQuery as useChatbotAuthQuery } from "@faire/web-api--source/endpoints/www/api/help-center/chat-bot/auth/get-hooks";
import { trackHelpCenterAiChatbotWidgetOpenView } from "@faire/web-api--source/events/helpCenter/view/aiChatbotWidgetOpen";
import { BRAND_DECAGON_AI_CHATBOT_TOOLTIP_SEEN } from "@faire/web-api--source/indigofair/data/IUserMilestone/BrandMilestone/BRAND_DECAGON_AI_CHATBOT_TOOLTIP_SEEN";
import { RETAILER_DECAGON_AI_CHATBOT_TOOLTIP_SEEN } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/RETAILER_DECAGON_AI_CHATBOT_TOOLTIP_SEEN";
import { QueryParameters as SupportQueryParameters } from "@faire/web-api--source/routes/www/support";
import { route as supportSelfServeChatCsat } from "@faire/web-api--source/routes/www/support/self-serve/chat-csat";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useChatbotStaticMetadata } from "../useChatbotStaticMetadata";

import {
  DECAGON_SCRIPT_URL_PROD,
  DECAGON_SCRIPT_URL_STAGING,
  DecagonHandoffTags,
} from "./consts";
import { useEnvironment } from "./useEnvironment";

const TEN_MINUTES_IN_MS = 10 * 60 * 1000; // 10 minutes

export const useDecagon = () => {
  const { strictLocalize } = useStrictLocalization();
  const {
    faire_direct_widget_chat,
    trigger_va,
    trigger_order_va,
    brand_order_token,
  } = useQueryParams(SupportQueryParameters.parse);
  const retailerToken = getRetailerToken();
  const brandToken = getBrandToken();
  const isUserImpersonating = getIsUserImpersonating();
  const userToken = getUserToken();
  const location = useLocation();
  const pathname = location.pathname;
  const { hasAIChatbotAccess, isAccessControlLoading } = useAIChatbotAccess();
  const {
    data: chatbotAuthResponse,
    refetch: refetchChatbotAuth,
    isLoading: isChatbotAuthLoading,
  } = useChatbotAuthQuery({
    enabled: hasAIChatbotAccess,
  });
  const {
    data: articleLocationResponse,
    isLoading: isArticleLocationTagsLoading,
  } = useArticleLocationTagsQuery({
    enabled: hasAIChatbotAccess,
  });

  const metadata = useChatbotStaticMetadata();
  const { tags } = articleLocationResponse ?? {};
  const { signature, expires_at, user_token, jwt } = chatbotAuthResponse ?? {};

  const { setHasOccurred: setRetailerChatbotTooltipOccurred } =
    useRetailerMilestone(RETAILER_DECAGON_AI_CHATBOT_TOOLTIP_SEEN);
  const { setHasOccurred: setBrandChatbotTooltipOccurred } = useBrandMilestone(
    BRAND_DECAGON_AI_CHATBOT_TOOLTIP_SEEN
  );
  const env = useEnvironment();

  const setRefreshChatbotAuth = (expires_at: number | undefined) => {
    if (!expires_at) {
      return;
    }
    // check if current time is 10 minutes before expires_at
    const currentTime = new Date().getTime();
    const timeDifference = expires_at * 1000 - currentTime;
    if (timeDifference <= TEN_MINUTES_IN_MS) {
      refetchChatbotAuth().then(({ data }) => {
        const { signature, expires_at, user_token, jwt } = data ?? {};
        window.duet.setUserIdAuth(signature, expires_at);
        window.duet.setUserId(user_token);
        window.duet.setMetadata({
          ...metadata,
          token: jwt,
          env,
        });
      });
    }
  };

  useEffect(() => {
    if (pathname === supportSelfServeChatCsat) {
      return;
    }

    if (
      isAccessControlLoading ||
      isArticleLocationTagsLoading ||
      isChatbotAuthLoading
    ) {
      return;
    }

    // If the user is not impersonating and we don't have a jwt, we don't want to load the chatbot
    if (!isUserImpersonating && !jwt) {
      return;
    }

    try {
      if (hasAIChatbotAccess) {
        const script = document.createElement("script");
        script.src = isProduction()
          ? DECAGON_SCRIPT_URL_PROD
          : DECAGON_SCRIPT_URL_STAGING;
        script.async = true;
        document.body.appendChild(script);
        script.addEventListener("load", () => {
          const decagonMetadata = {
            ...metadata,
            user_article_location_tags: tags,
            token: jwt,
            env,
          };
          if (faire_direct_widget_chat === "1") {
            decagonMetadata.handoff_tags =
              DecagonHandoffTags.BrandFaireDirectWidgetChat;
          }
          window.duet.setMetadata(decagonMetadata);
          window.duet.setUserId(user_token ?? userToken);
          window.duet.setUserIdAuth(signature, expires_at);
          window.duet.setChatSessionDuration(1); // set the chat session duration to 1 hour
          window.duet.setEventListener((event) => {
            if (event.eventName === "DUET_INITIALIZE") {
              if (isRetailer()) {
                setRetailerChatbotTooltipOccurred();
              } else {
                setBrandChatbotTooltipOccurred();
              }
              trackHelpCenterAiChatbotWidgetOpenView(
                retailerToken ?? "",
                brandToken ?? "",
                pathname,
                ""
              );
              setRefreshChatbotAuth(expires_at);
            }
            if (event.eventName === "DUET_USER_INPUT") {
              setRefreshChatbotAuth(expires_at);
            }
          });
          if (!isProduction()) {
            window.duet.setIsConversationUrlVisible(true);
          }
          if (faire_direct_widget_chat === "1") {
            window.duet.setTriggerMessage(
              strictLocalize({
                defaultMessage:
                  "Can I answer any questions on adding the Faire Direct Widget to your website?",
                description: {
                  img: "https://cdn.faire.com/fastly/5b1833390b8d88d98867200420fcbbacd5fa64aedd2a7b42adc19dd2b1a3fc4c.png",
                  text: "Initial message when engaging chatbot for help installing the Faire Direct Widget on a brand's website",
                },
              })
            );
            window.duet.setInitialUserMessage(
              strictLocalize({
                defaultMessage:
                  "Yes, please connect me with live chat support.",
                description: {
                  img: "https://cdn.faire.com/fastly/c7e00ef543c773eba04bbd5c4df51787b3e76a7eaa4baf0dfd69296be934536c.png",
                  text: "Pre-populated message when engaging chatbot for help installing the Faire Direct Widget on a brand's website",
                },
              })
            );
            window.duet.open();
          } else {
            if (brand_order_token) {
              window.duet.startNewConversation();
              window.duet.setTriggerMessage(
                strictLocalize(
                  {
                    defaultMessage:
                      "Hello! It looks like you need help with Order #{orderNumber}. I'll do my best to assist, or connect you with a support specialist. Can you describe what's happening?",
                    description: {
                      text: "Initial message when engaging chatbot for order support",
                    },
                  },
                  {
                    orderNumber: orderNumber(brand_order_token),
                  }
                )
              );
            }
            if (trigger_va === "true" || trigger_order_va === "true") {
              window.duet.open();
            }
          }
        });
        return () => {
          document.body.removeChild(script);
        };
      }
    } catch (error) {
      logError(`Error loading Decagon script, ${error}`, {
        data: {
          pathname,
          retailerToken,
          brandToken,
          metadata,
        },
        autoFingerprint: true,
      });
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAIChatbotAccess, isArticleLocationTagsLoading, isChatbotAuthLoading]);
};
